import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { BaseAPI, Mapper } from '@core/services';
import { MeasurementUnit } from '@shared/models/measurement-unit.model';
import { APIConstants } from '@shared/constants';
import { LookupType } from '../enums/shared-enums.enum';
import { Lookup } from '../models/Lookup.model';
import { MemoizeService } from '@shared/services/memoize.service';
import { ClassConstructor } from 'class-transformer';

@Injectable({
  providedIn: 'root',
})
export class LookupService {
  constructor(private baseAPI: BaseAPI, private memoizeService: MemoizeService, private mapper: Mapper) {}

  getMeasurementUnits(): Observable<MeasurementUnit[]> {
    return this.memoize<MeasurementUnit[]>('measurementUnit', () => this.baseAPI.get(APIConstants.GET_ALL_MEASUREMENT_UNITS)).pipe(
      map((res: any) => {
        return this.mapper.fromJson(MeasurementUnit, res.data);
      })
    );
  }

  getLookup<T = Lookup>(
    lookup: LookupType,
    { searchTerm, ...options }: any,
    forceCleanCache?: boolean,
    Model?: ClassConstructor<T>
  ): Observable<T[]> {
    const rawParams = { ...options };
    const params: any = Object.keys(rawParams).reduce((group, key) => {
      if (rawParams[key] !== null && typeof rawParams[key] !== 'undefined') {
        group[key] = rawParams[key];
      }
      return group;
    }, {});

    if (searchTerm) {
      params.searchTerm = searchTerm;
    }

    const cacheId = this.memoizeService.hashInputs(params, lookup);

    if (forceCleanCache) {
      params.ignoreCache = true;
      this.resetCache();
    }

    return this.memoize<any>(cacheId, () => this.baseAPI.get(
      lookup.startsWith(APIConstants.API_URL_BASE) 
        ? lookup 
        : `${APIConstants.GET_LOOKUPS}${lookup}`, { params }))
      .pipe(map((res) => this.mapper.fromJson(Model || Lookup, res.data))
    );
  }

  getGlobalSearchLookup(
    lookup: LookupType,
    { searchTerm, ...options }: any,
    forceCleanCache?: boolean,
    Model: any = Lookup
  ): Observable<any> {
    const rawParams = { ...options };
    const params: any = Object.keys(rawParams).reduce((group, key) => {
      if (rawParams[key] !== null && typeof rawParams[key] !== 'undefined') {
        group[key] = rawParams[key];
      }
      return group;
    }, {});

    if (searchTerm) {
      params.searchTerm = searchTerm;
    }

    const cacheId = this.memoizeService.hashInputs(params, lookup);

    if (forceCleanCache) {
      this.resetCache();
    }

    return this.memoize<any>(cacheId, () => this.baseAPI.get(`${APIConstants.GET_GLOBAL_SEARCH_LOOKUPS}${lookup}`, { params })).pipe(
      map((res) => this.mapper.fromJson(Model, res.data))
    );
  }

  private memoize<T = any>(id, requestCallBack): Observable<T> {
    return this.memoizeService.memoize<T>(id, requestCallBack);
  }

  private resetCache(): void {
    this.memoizeService.resetCache();
  }
}
